/* global YT */

const SELECTOR = '.js-header-video';

const isReducedMotion = window.matchMedia(`(prefers-reduced-motion: reduce)`) === true || window.matchMedia(`(prefers-reduced-motion: reduce)`).matches === true;

function getYouTubeId(url) {
    if (!url) {
        return null;
    }

    const regex = /(?:youtube\.com\/(?:.*[?&]v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

    const match = url.match(regex);

    return match ? match[1] : null;
}

async function loadYouTubeAPI() {
    return new Promise(resolve => { // eslint-disable-line
        const tag = document.createElement('script');

        tag.src = 'https://www.youtube.com/iframe_api';

        window.onYouTubeIframeAPIReady = () => {
            resolve();
            delete window.onYouTubeIframeAPIReady;
        }

        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    });
}

async function getFirstVideoJsPlayer(videojs, interval = 100, maxRetries = 10) {
    return new Promise((resolve) => { // eslint-disable-line
        const check = retriesLeft => {
            const players = videojs.getPlayers();
            const keys = Object.keys(players);

            if (keys.length) {
                return resolve(players[keys[0]]);
            }

            if (retriesLeft <= 0) {
                return resolve(null);
            }

            setTimeout(() => check(retriesLeft - 1), interval);
        };

        check(maxRetries);
    });
}

const determineIframeOrigin = src => {
    const a = document.createElement('a');
    a.href = src;
    return a.protocol + '//' + a.hostname + (a.port ? ':' + a.port : '');
}

export async function init() {
    const headerElement = document.querySelector(SELECTOR);

    if (!headerElement) {
        return;
    }

    const videoContainerElement = headerElement.querySelector('.video-container');

    if (!videoContainerElement) {
        return;
    }

    const playButtons = headerElement.querySelectorAll('.page-hero__play');

    const toggleButtonState = isPlaying => {
        for (const playButton of playButtons) {
            playButton.querySelector('div').innerHTML = isPlaying
                ? '<svg class="o-icon o-icon--circle-pause"><use xlink:href="#circle-pause"></use></svg>'
                : '<svg class="o-icon o-icon--play-circle"><use xlink:href="#play-circle"></use></svg>';

            playButton.querySelector('span').textContent = isPlaying ? 'Pause' : 'Play';
        }
    }

    const videoUrl = videoContainerElement.dataset.url;

    if (videoUrl) {
        const videoId = getYouTubeId(videoUrl);

        if (!videoId) {
            return;
        }

        videoContainerElement.id = `youtube-${videoId}`;

        await loadYouTubeAPI();

        const player = new YT.Player(videoContainerElement.id, {
            videoId,
            playerVars: {
                controls: 0,
                autoplay: 0,
                mute: 1,
                disablekb: 1,
                enablejsapi: 1,
                iv_load_policy: 3,
                modestbranding: 1,
                playsinline: 1,
                showinfo: 0,
                rel: 0,
                height: 1200,
                width: 675,
                autohide: 1,
                loop: 1,
                playlist: videoId,
            },
            events: {
                onReady: () => {
                    videoContainerElement.classList.add('video-container--loaded');

                    for (const playButton of playButtons) {
                        playButton.addEventListener('click', () => {
                            if (player.getPlayerState() === YT.PlayerState.PLAYING) {
                                player.pauseVideo();
                            } else {
                                player.playVideo();
                            }
                        });
                    }

                    if (!isReducedMotion) {
                        player.playVideo();
                    }
                },
                onStateChange: event => {
                    if (event.data === YT.PlayerState.ENDED) {
                        // if loop doesn't work
                        // player.seekTo(0);
                        // player.playVideo();
                    } else if (event.data === YT.PlayerState.PLAYING) {
                        toggleButtonState(true);
                    } else if (event.data === YT.PlayerState.PAUSED) {
                        toggleButtonState(false);
                    }
                },
            },
        });
    } else {
        const iframe = headerElement.querySelector('iframe[src*="brightcove"]');

        if (!iframe) {
            return;
        }

        const contentWindow = iframe.contentWindow;

        const player = await getFirstVideoJsPlayer(contentWindow.videojs);

        if (!player) {
            return;
        }

        // Wait for videojs to be ready
        await new Promise(resolve => {
            player.ready(() => {
                resolve();
            });
        });

        videoContainerElement.classList.add('video-container--loaded');

        for (const playButton of playButtons) {
            playButton.addEventListener('click', () => {
                if (player.paused()) {
                    player.play();
                } else {
                    player.pause();
                }
            });
        }

        player.on('playing', () => {
            toggleButtonState(true);
        });

        player.on('pause', () => {
            toggleButtonState(false);
        });

        player.on('ended', () => {
            player.play();
        });

        if (!isReducedMotion) {
            player.play();
        }
    }
}
